<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FormInput from 'v3/components/Form/Input.vue';
import NativeSelect from 'dashboard/components-next/native-select/NativeSelect.vue';
import Auth from '../../../../api/auth';
import { getHelpUrlForFeature } from 'dashboard/helper/featureHelper';

const emit = defineEmits(['close']);

const dialogRef = ref(null);
const store = useStore();
const { t } = useI18n();

const agentId = ref(null);
const provider = ref('');
const agentName = ref('');
const agentAvailability = ref('');
const selectedRoleId = ref(null);
const agentEmail = ref('');
const restrictContactVisibility = ref(false);
const contactVisibilityScope = ref('user');
const advancedOpen = ref(false);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const helpURL = computed(() => getHelpUrlForFeature('agents'));

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'owner',
      name: 'owner',
      label: t('AGENT_MGMT.AGENT_TYPES.OWNER'),
    },
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRolesList = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRolesList];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const isAgentRole = computed(() => {
  const roleName = selectedRole.value?.name || '';
  return roleName === 'agent' || roleName.startsWith('custom_');
});

function resetForm() {
  agentId.value = null;
  provider.value = '';
  agentName.value = '';
  agentAvailability.value = '';
  selectedRoleId.value = null;
  agentEmail.value = '';
  restrictContactVisibility.value = false;
  contactVisibilityScope.value = 'user';
  advancedOpen.value = false;
}

function close() {
  dialogRef.value?.close();
}

function handleDialogClose() {
  resetForm();
  v$.value.$reset();
  emit('close');
}

function open(agent) {
  if (!agent?.id) return;

  agentId.value = agent.id;
  provider.value = agent.provider || '';
  agentName.value = agent.name || '';
  agentAvailability.value = agent.availability_status || '';
  selectedRoleId.value = agent.custom_role_id || agent.role;
  agentEmail.value = agent.email || '';
  restrictContactVisibility.value =
    agent.restrictions?.restrict_contact_visibility || false;
  contactVisibilityScope.value =
    agent.restrictions?.contact_visibility_scope || 'user';

  dialogRef.value?.open();
}

defineExpose({ open, close });

function suppressEnterSubmit(e) {
  if (e.target?.tagName === 'TEXTAREA') return;
  e.preventDefault();
}

async function editAgent() {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (!selectedRole.value) return;

  try {
    const payload = {
      id: agentId.value,
      name: agentName.value,
      availability: agentAvailability.value,
      restrictions: {
        restrict_contact_visibility:
          isAgentRole.value && restrictContactVisibility.value,
        contact_visibility_scope: contactVisibilityScope.value,
      },
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    close();
  } catch {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
}

async function resetPassword() {
  try {
    await Auth.resetPassword({ email: agentEmail.value });
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('AGENT_MGMT.EDIT_MODAL.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    width="3xl"
    @close="handleDialogClose"
  >
    <template #description>
      <p
        class="mb-0 border-b border-n-weak pb-4 text-sm leading-relaxed text-n-slate-11"
      >
        {{ t('AGENT_MGMT.EDIT_MODAL.INTRO') }}
      </p>
    </template>

    <form
      class="grid w-full gap-5"
      @submit.prevent="editAgent"
      @keydown.enter.exact="suppressEnterSubmit"
    >
      <FormInput
        v-model="agentName"
        name="edit-agent-name"
        spacing="compact"
        :label="t('AGENT_MGMT.EDIT.FORM.NAME.LABEL')"
        :placeholder="t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
        :has-error="v$.agentName.$error"
        :error-message="
          v$.agentName.$error ? v$.agentName.$errors[0].$message : ''
        "
        @blur="v$.agentName.$touch"
      />

      <div class="grid gap-5 md:grid-cols-2">
        <FormInput
          v-model="agentEmail"
          name="edit-agent-email"
          spacing="compact"
          readonly
          disabled
          :label="t('AGENT_MGMT.EDIT_MODAL.EMAIL_LABEL')"
          :placeholder="t('AGENT_MGMT.EDIT.FORM.EMAIL.PLACEHOLDER')"
        />

        <div class="flex min-w-0 flex-col">
          <label
            class="mb-0 flex justify-between text-sm font-medium leading-6 text-n-slate-12"
            :class="{ 'text-n-ruby-12': v$.selectedRoleId.$error }"
            for="edit-agent-access-level"
          >
            {{ t('AGENT_MGMT.EDIT_MODAL.ACCESS_LEVEL_LABEL') }}
          </label>
          <div class="mt-1">
            <NativeSelect
              id="edit-agent-access-level"
              v-model="selectedRoleId"
              :has-error="v$.selectedRoleId.$error"
              @change="v$.selectedRoleId.$touch"
            >
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.label }}
              </option>
            </NativeSelect>
          </div>
          <p
            v-if="v$.selectedRoleId.$error"
            class="mt-1.5 text-sm text-n-ruby-9"
          >
            {{ t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </p>
        </div>
      </div>

      <p class="mb-0 text-sm leading-relaxed text-n-slate-11">
        {{ t('AGENT_MGMT.EDIT_MODAL.ROLE_HELPER') }}
      </p>

      <div
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-3"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 text-left text-sm font-semibold text-n-blue-11"
          @click="advancedOpen = !advancedOpen"
        >
          {{ t('AGENT_MGMT.EDIT_MODAL.ADVANCED_TITLE') }}
          <Icon
            :icon="
              advancedOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="size-5 shrink-0 text-n-blue-11"
          />
        </button>

        <div
          v-show="advancedOpen"
          class="flex flex-col gap-4 border-t border-n-weak pt-4"
        >
          <section v-if="isAgentRole" class="flex flex-col gap-3">
            <div
              class="flex flex-col gap-3 md:flex-row md:items-start md:gap-6"
            >
              <label
                class="flex min-w-0 flex-1 cursor-pointer items-start gap-3"
              >
                <input
                  v-model="restrictContactVisibility"
                  type="checkbox"
                  class="mt-1 size-4 shrink-0 rounded border-n-weak text-n-brand"
                />
                <span class="flex min-w-0 flex-col gap-1">
                  <span class="text-sm font-semibold text-n-slate-12">
                    {{ $t('AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.LABEL') }}
                  </span>
                  <span class="text-sm leading-relaxed text-n-slate-11">
                    {{
                      $t(
                        'AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.DESCRIPTION'
                      )
                    }}
                  </span>
                </span>
              </label>

              <div
                v-if="restrictContactVisibility"
                class="flex w-full min-w-0 flex-col gap-1 md:w-64 md:flex-shrink-0"
              >
                <label
                  class="mb-0 text-sm font-semibold leading-6 text-n-slate-12"
                  for="edit-agent-contact-scope"
                >
                  {{
                    $t('AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.SCOPE_LABEL')
                  }}
                </label>
                <NativeSelect
                  id="edit-agent-contact-scope"
                  v-model="contactVisibilityScope"
                >
                  <option value="team">
                    {{
                      $t(
                        'AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.SCOPE_TEAM'
                      )
                    }}
                  </option>
                  <option value="user">
                    {{
                      $t(
                        'AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.SCOPE_USER'
                      )
                    }}
                  </option>
                </NativeSelect>
              </div>
            </div>
          </section>

          <Button
            v-if="provider !== 'saml'"
            ghost
            blue
            type="button"
            icon="i-lucide-lock-keyhole"
            class="w-fit !px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
      </div>
    </form>

    <template #footer>
      <div
        class="flex w-full flex-wrap items-center justify-between gap-4 border-t border-n-weak pt-4"
      >
        <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
          <a
            v-if="helpURL"
            :href="helpURL"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-sm font-semibold text-n-blue-11 hover:underline"
          >
            {{ $t('AGENT_MGMT.EDIT_MODAL.LEARN_MORE_LINK') }}
            <Icon
              icon="i-lucide-chevron-right"
              class="size-4 shrink-0 text-n-blue-11"
            />
          </a>
        </CustomBrandPolicyWrapper>
        <div v-if="!helpURL" />

        <div class="flex items-center gap-3">
          <Button
            variant="link"
            slate
            type="button"
            class="!font-bold"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click="close"
          />
          <Button
            blue
            type="button"
            class="rounded-xl !font-bold"
            :label="$t('AGENT_MGMT.EDIT_MODAL.UPDATE')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
            @click="editAgent"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
